/**
 * GestureChannel — one-shot body-gesture layer.
 *
 * Plays a single body clip on top of whatever the blueprint graph is driving,
 * with a short fade-in, hold, and fade-out. Used to layer gestures like
 * "lean in", "laugh", "sigh" onto the current SM state (idle / listening /
 * responding) while LiveLink still drives the face.
 *
 * Lifecycle per gesture:
 *   t = 0 ............... play() called, start = now
 *   0 .. fadeInMs ....... weight ramps 0 → peakWeight
 *   fadeInMs .. fadeOutFrom .. weight held at peakWeight
 *   fadeOutFrom .. totalMs .. weight ramps peakWeight → 0
 *   t > totalMs ......... channel cleared; gesture action gets a final weight=0 + stop()
 *
 * `apply(actions)` is called by GraphEvaluator.applyWeights every frame,
 * AFTER the main weight loop, so the gesture weight is not overwritten by
 * the graph's per-frame weight reset.
 */

import { getBodyClipLoader } from './bodyClipLoaderRef.js'

const DEFAULT_PEAK_WEIGHT = 0.7
const DEFAULT_FADE_MS     = 250

// Live state — module-scoped, only one gesture at a time. A new play() call
// replaces whatever was in flight.
let _active = null

export function playBodyGesture(clipName, {
  durationMs = 3000,
  peakWeight = DEFAULT_PEAK_WEIGHT,
  fadeInMs   = DEFAULT_FADE_MS,
  fadeOutMs  = DEFAULT_FADE_MS,
} = {}) {
  if (!clipName) return
  // A conversational override (BodyMotion locomotion, or a performance-
  // classified generated clip — both routed through playGeneratedClip with
  // followAction: true) already owns the skeleton exclusively; applyWeights
  // zeroes any additive gesture weight while it's active anyway, but starting
  // a NEW envelope here would leave it primed to pop back in for the tail of
  // its fade-out once the override releases. Scoped to followAction so
  // dev/test direct-plays (prefab Test button, Clips preview) — which never
  // set it — are unaffected.
  if (typeof window !== 'undefined' && window.__DIRECT_PLAY__?.followAction) return
  // JIT: gesture metadata arrives 1–3s before the audio, and gesture clips load
  // in the background (not in the priority set). Pull this clip to the front of
  // the load queue now so it's registered by the time the envelope ramps up.
  getBodyClipLoader()?.request(clipName)
  const now = performance.now()
  _active = {
    clipName,
    startTime:   now,
    fadeInUntil: now + fadeInMs,
    fadeOutFrom: now + Math.max(fadeInMs, durationMs - fadeOutMs),
    endTime:     now + durationMs,
    peakWeight,
  }
}

export function stopBodyGesture() {
  _active = null
}

export function isActive() {
  return _active !== null
}

/** The active gesture's clip and envelope weight now, or null when nothing plays.
 *
 *  Split out of applyGesture because the splat's part rig takes weights, not mixer
 *  actions. `done` is the final tick — the caller zeroes and calls stopBodyGesture. */
export function gestureEnvelope(now = performance.now()) {
  if (!_active) return null
  const a = _active
  if (now >= a.endTime) return { clipName: a.clipName, weight: 0, done: true }
  if (now < a.fadeInUntil) {
    return { clipName: a.clipName, weight: a.peakWeight * ((now - a.startTime) / (a.fadeInUntil - a.startTime)), done: false }
  }
  if (now < a.fadeOutFrom) return { clipName: a.clipName, weight: a.peakWeight, done: false }
  const t = (now - a.fadeOutFrom) / (a.endTime - a.fadeOutFrom)
  return { clipName: a.clipName, weight: a.peakWeight * (1 - t), done: false }
}

/**
 * Called every frame by applyWeights after the main weight loop.
 * Mutates the mixer action's weight to match the current envelope phase.
 */
export function applyGesture(actions) {
  const env = gestureEnvelope()
  if (!env) return
  const action = actions?.[env.clipName]
  if (!action) {
    // Mapped clip not in the mixer yet. Under JIT loading the gesture clip may
    // still be streaming in — pull it to the front of the queue and keep the
    // channel alive so it plays once it lands. Only give up if the gesture's
    // envelope has already elapsed (too late to be worth playing).
    if (env.done) { _active = null; return }
    getBodyClipLoader()?.request(env.clipName)
    return
  }

  if (env.done) {
    // Finished — zero it and clear the channel.
    if (action.isRunning()) action.stop()
    action.setEffectiveWeight(0)
    _active = null
    return
  }

  // Gesture plays additively over the base state so the underlying SM clip
  // (idle / responding / …) still drives the skeleton's resting pose; the
  // gesture layers limb motion on top. LoopOnce so it plays through exactly
  // once over its duration.
  action.blendMode = 2501 /* THREE.AdditiveAnimationBlendMode */
  action.setLoop(2200 /* THREE.LoopOnce */, 1)
  action.clampWhenFinished = true
  action.setEffectiveWeight(env.weight)
  if (!action.isRunning()) {
    action.reset()
    action.play()
  }
}

// Dev / test hook — tests read the current channel state
if (typeof window !== 'undefined') {
  window.__GESTURE_CHANNEL__ = {
    get active() { return _active },
    play:  playBodyGesture,
    stop:  stopBodyGesture,
  }
}
